import React, { useState, useEffect, useRef } from 'react';
import ThumbnailPicker, { uploadThumbnail } from '../components/ThumbnailPicker';
import { useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { createPost, createPulse, deletePulse, getPulseById, getSettings, getUserByUsername, likePulse, unlikePulse, listFollowing, listPulse, updatePulse, viewPulse } from '../utils/secureFunctions';
import { getMyPhoto } from '../utils/myProfile';
import TopNavigation from '../components/TopNavigation';
import CommentsInline from '../components/CommentsInline';
import ReelEditorV2 from '../components/ReelEditorV2';
import QuickTrimModal from '../components/QuickTrimModal';
import AdSlot from '../components/AdSlot';
import type { MusicTrack } from '../components/MusicLibraryPicker';
import { trackContentView, trackInteraction } from '../utils/activityTracking';
import { extractHashtags, extractMentions, renderTextWithLinks } from '../utils/text';
import { createNotification } from '../utils/notificationHelpers';
import { uploadModeratedMedia } from '../utils/moderatedUpload';
import { describeModerationError, notifyIfHeldForReview } from '../utils/moderationErrors';
import ImageWithFallback from '../components/ImageWithFallback';
import SignedVideo from '../components/SignedVideo';
import AmbientBackdrop from '../components/AmbientBackdrop';
import WatchTracker from '../components/WatchTracker';
import UserLink from '../components/UserLink';

interface PulseVideo {
  id: string;
  userId: string;
  userName: string;
  userPhoto: string;
  videoURL: string;
  thumbnailURL?: string;
  caption: string;
  category: string;
  likeCount: number;
  likedByMe?: boolean;
  comments: number;
  views: number;
  createdAt: any;
}

type Category = 'For You' | 'My Videos' | 'Messages' | 'Music' | 'Motivation' | 'Drama' | 'Empowerment' | 'Marriage' | 'Entertainment' | 'Kids Corner' | 'Others';

const categories: Category[] = ['For You', 'My Videos', 'Messages', 'Music', 'Motivation', 'Drama', 'Empowerment', 'Marriage', 'Entertainment', 'Kids Corner', 'Others'];
const PLAYBACK_SPEEDS: number[] = [0.5, 0.75, 1, 1.25, 1.5];

const Pulse: React.FC = () => {
  const [videos, setVideos] = useState<PulseVideo[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<Category>('For You');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAdOverlay, setShowAdOverlay] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  // Background music audio element (synced with current video)
  const musicAudioRef = useRef<HTMLAudioElement | null>(null);
  const [volume] = useState<number>(() => {
    const saved = parseFloat(localStorage.getItem('pulseVolume') || '1');
    return isNaN(saved) ? 1 : Math.min(1, Math.max(0, saved));
  });
  const lastNonZeroVolumeRef = useRef<number>(1);
  // Repost state
  const [showRepostModal, setShowRepostModal] = useState(false);
  const [repostTarget, setRepostTarget] = useState<PulseVideo | null>(null);
  const [repostQuote, setRepostQuote] = useState('');
  const [postingRepost, setPostingRepost] = useState(false);
  // Edit/Delete state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOwnerMenu, setShowOwnerMenu] = useState(false);
  // 'cover' for near-9:16 clips (fills the reel); 'contain' + ambient backdrop for other shapes.
  const [videoFit, setVideoFit] = useState<Record<string, 'cover' | 'contain'>>({});
  const [editCaption, setEditCaption] = useState('');
  const [editThumb, setEditThumb] = useState<File | null>(null);
  const [editAudience, setEditAudience] = useState<'public' | 'friends'>('public');
  const [editCategory, setEditCategory] = useState<Category>('Messages');
  // Prefetcher for smooth swipes (Option A)
  const preloadedVideosRef = useRef<Map<string, HTMLVideoElement>>(new Map());
  const viewedOnceRef = useRef<Set<string>>(new Set());
  const pendingAudioStartRef = useRef<boolean>(false);
  const userInteractedRef = useRef<boolean>(false);
  const location = useLocation();
  const [isMobile] = useState<boolean>(() => {
    try { return window.innerWidth <= 768; } catch { return false; }
  });
  // Mobile caption/action overlay visibility (auto-hide to prevent covering video)
  const [showInfoOverlay] = useState(true);
  const [playbackRate, setPlaybackRate] = useState<number>(() => {
    const stored = parseFloat(localStorage.getItem('pulsePlaybackRate') || '1');
    if (isNaN(stored)) return 1;
    return Math.min(2, Math.max(0.5, stored));
  });
  const [showPlaybackOptions, setShowPlaybackOptions] = useState(false);

  const loadVideos = React.useCallback((category: Category) => {
    let mounted = true;
    const run = async () => {
      try {
        let mode: 'forYou' | 'my' | 'category' = 'forYou';
        let categoryParam: string | undefined;
        if (category === 'My Videos') {
          mode = 'my';
        } else if (category === 'For You') {
          mode = 'forYou';
        } else {
          mode = 'category';
          categoryParam = category;
        }

        const res = await listPulse({ limit: 50, mode, category: categoryParam });
        if (!mounted) return;
        setVideos(res.pulses as any);
        setCurrentIndex(0);
      } catch (err) {
        console.error('Pulse feed error:', err);
      }
    };

    run();
    const t = window.setInterval(run, 20000);
    return () => {
      mounted = false;
      window.clearInterval(t);
    };
  }, []);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const vid = sp.get('videoId');
    if (vid) {
      // Deep link present; skip normal loading
      return;
    }
    const cleanup = loadVideos(selectedCategory);
    return cleanup;
  }, [selectedCategory, loadVideos, location.search]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Auto-play current video and setup auto-advance
    const video = videoRefs.current[currentIndex];
    const vidData = videos[currentIndex];
    if (video) {
      // Avoid aggressive load/play loops: only attempt play once video can play
      try {
        video.volume = volume;
        video.muted = volume === 0;
      } catch {}
      const attemptPlay = () => {
        if (!video) return;
        if (video.readyState >= 2) { // HAVE_CURRENT_DATA
          const p = video.play();
          if (p) {
            p.catch(() => {
              // Suppress autoplay warnings; user gesture will trigger later
            });
          }
        }
      };
      if (video.readyState >= 2) {
        attemptPlay();
      } else {
        const onCanPlay = () => {
          attemptPlay();
          video.removeEventListener('canplay', onCanPlay);
        };
        video.addEventListener('canplay', onCanPlay, { once: true });
      }

      // Track view for personalization and music royalties
      if (vidData) {
        try {
          trackContentView(vidData.id, 'reels', vidData.category);
        } catch {}
      }
      
      // Keep background music in sync with video playback
      const handlePlay = () => {
        const a = musicAudioRef.current;
        if (a) {
          try {
            // Align audio time to video time to avoid drift
            if (Math.abs(a.currentTime - (video.currentTime || 0)) > 0.25) {
              a.currentTime = video.currentTime || 0;
            }
            a.volume = volume;
            a.muted = volume === 0;
            if (video.readyState >= 2) {
              const p = a.play();
              p?.catch(() => {
                // Autoplay might be blocked; defer until user interacts
              });
            }
          } catch {}
        }
      };
      const handlePause = () => {
        try { musicAudioRef.current?.pause(); } catch {}
      };
      const handleSeeking = () => {
        const a = musicAudioRef.current;
        if (a) {
          try { a.currentTime = video.currentTime || 0; } catch {}
        }
      };
      // Auto-advance to next video when current ends
      const handleVideoEnd = () => {
        // Show mid-roll ad before advancing
        const v = videoRefs.current[currentIndex];
        try { v?.pause(); } catch {}
        setShowAdOverlay(true);
      };
      
      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      video.addEventListener('seeking', handleSeeking);
      video.addEventListener('ended', handleVideoEnd);
      
      // Cleanup
      return () => {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('seeking', handleSeeking);
        video.removeEventListener('ended', handleVideoEnd);
      };
    }

    // Pause other videos and unload them to save memory
    videoRefs.current.forEach((v, index) => {
      if (index !== currentIndex && v) {
        v.pause();
        // Unload video data for videos far from current index to save memory
        if (Math.abs(index - currentIndex) > 2) {
          v.src = '';
          v.load();
        }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, videos]);

  // Load background music for current video (if any) and prepare audio element
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const setupAudio = async () => {
      // Cleanup any previous audio
      try { musicAudioRef.current?.pause(); } catch {}
      musicAudioRef.current = null;

      const v = videos[currentIndex];
      const bg: any = v && (v as any).bgMusic;
      if (!v || !bg?.id) {
        return; // no background music
      }
      try {
        // Prefer embedded audioURL on the pulse to avoid permission issues
        const audioURL: string | undefined = bg?.audioURL;
        if (!audioURL) return;
        const audio = new Audio(audioURL);
        audio.loop = true;
        audio.preload = 'auto';
        audio.crossOrigin = 'anonymous';
        audio.volume = volume;
        audio.muted = volume === 0;
        audio.playbackRate = playbackRate;
        // align with current video time
        const ve = videoRefs.current[currentIndex];
        if (ve && !isNaN(ve.currentTime)) {
          try { audio.currentTime = ve.currentTime || 0; } catch {}
        }
        musicAudioRef.current = audio;
        // If video is already playing OR we have pending start from user click, try to start audio
        if ((ve && !ve.paused) || pendingAudioStartRef.current || userInteractedRef.current) {
          try { await audio.play(); } catch {}
          pendingAudioStartRef.current = false;
        }
      } catch (e) {
        console.warn('Failed to setup background music', e);
      }
    };
    setupAudio();
    // Cleanup on index/video change
    return () => {
      try { musicAudioRef.current?.pause(); } catch {}
      musicAudioRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, videos]);

  // Persist and apply volume changes
  useEffect(() => {
    localStorage.setItem('pulseVolume', String(volume));
    const v = videoRefs.current[currentIndex];
    if (v) {
      try { v.volume = volume; v.muted = volume === 0; } catch {}
    }
    const a = musicAudioRef.current;
    if (a) {
      try { a.volume = volume; a.muted = volume === 0; } catch {}
    }
    if (volume > 0) {
      lastNonZeroVolumeRef.current = volume;
    }
  }, [volume, currentIndex]);

  useEffect(() => {
    localStorage.setItem('pulsePlaybackRate', String(playbackRate));
    const v = videoRefs.current[currentIndex];
    if (v) {
      try { v.playbackRate = playbackRate; } catch {}
    }
    const a = musicAudioRef.current;
    if (a) {
      try { a.playbackRate = playbackRate; } catch {}
    }
  }, [playbackRate, currentIndex]);

  // Prefetch next/previous videos around the current index to reduce buffering on swipe
  useEffect(() => {
    const preloaded = preloadedVideosRef.current;
    const needed = new Set<string>();
    const add = (idx: number) => {
      const url = videos[idx]?.videoURL;
      if (!url) return;
      needed.add(url);
      if (!preloaded.has(url)) {
        try {
          const v = document.createElement('video');
          v.preload = 'auto';
          v.src = url;
          // Don’t attach to DOM; just warm the cache/connection
          v.load();
          preloaded.set(url, v);
        } catch {}
      }
    };

    // Preload the immediate neighbors only (±1) to keep memory in check
    if (videos.length > 0) {
      if (currentIndex + 1 < videos.length) add(currentIndex + 1);
      if (currentIndex - 1 >= 0) add(currentIndex - 1);
    }

    // Cleanup any entries no longer near the viewport
    preloaded.forEach((v, url) => {
      if (!needed.has(url)) {
        try { (v as any).pause?.(); v.removeAttribute('src'); v.load(); } catch {}
        preloaded.delete(url);
      }
    });
  }, [currentIndex, videos]);


  const handleLike = async (video: PulseVideo) => {
    if (!auth.currentUser) return;
    const isLiked = !!video.likedByMe;

    try {
      if (isLiked) {
        await unlikePulse(video.id);
      } else {
        await likePulse(video.id);
      }
      setVideos((prev) => prev.map((v) => {
        if (v.id !== video.id) return v;
        const nextLiked = !isLiked;
        const nextCount = Math.max(0, (v.likeCount || 0) + (nextLiked ? 1 : -1));
        return { ...v, likedByMe: nextLiked, likeCount: nextCount } as PulseVideo;
      }));
      trackInteraction(video.id, 'like');
    } catch (error) {
      console.error('Like failed:', error);
    }
  };

  const handleSkip = (seconds: number) => {
    const video = videoRefs.current[currentIndex];
    if (!video) return;
    userInteractedRef.current = true;
    const duration = video.duration;
    const current = Number.isFinite(video.currentTime) ? video.currentTime : 0;
    let target = current + seconds;
    if (!Number.isFinite(target)) {
      target = current;
    }
    if (target < 0) target = 0;
    if (Number.isFinite(duration) && duration && duration > 0) {
      const maxTime = Math.max(0, duration);
      if (target > maxTime) target = duration;
    }
    try { video.currentTime = target; } catch {}
    const audio = musicAudioRef.current;
    if (audio) {
      try { audio.currentTime = target; } catch {}
    }
  };


  const handleDeleteCurrent = async () => {
    const video = currentVideo;
    if (!video || !window.confirm('Delete this Pulse permanently?')) return;
    try {
      await deletePulse(video.id);
      setShowOwnerMenu(false);
      setVideos((prev) => prev.filter((v) => v.id !== video.id));
      setCurrentIndex((i) => Math.max(0, Math.min(i, videos.length - 2)));
    } catch (e) {
      console.error('Delete pulse failed', e);
      alert('Failed to delete Pulse');
    }
  };

  const renderActionButtons = () => {
    const video = currentVideo;
    if (!video) return null;
    const isLiked = !!video.likedByMe;
    const likeCount = video.likeCount || 0;
    const commentCount = typeof video.comments === 'number' ? video.comments : 0;
    const isOwner = !!auth.currentUser && video.userId === auth.currentUser.uid;
    const item = (key: string, icon: string, label: string | number, onClick: () => void, aria: string) => (
      <button
        key={key}
        type="button"
        aria-label={aria}
        style={styles.railButton}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
      >
        <span style={styles.railIcon}>{icon}</span>
        <span style={styles.railLabel}>{label}</span>
      </button>
    );
    return (
      <div style={{ ...styles.actionRail, ...(isMobile ? styles.actionRailMobile : {}) }} onClick={(e) => e.stopPropagation()}>
        {item('like', isLiked ? '❤️' : '🤍', likeCount, () => handleLike(video), isLiked ? 'Unlike' : 'Like')}
        {item('comment', '💬', commentCount, () => setShowComments(true), 'Comments')}
        {item('play', '⚙️', 'Speed', () => setShowPlaybackOptions((prev) => !prev), 'Playback options')}
        {isOwner && item('more', '⋯', 'More', () => setShowOwnerMenu((v) => !v), 'More options')}
        {isOwner && showOwnerMenu && (
          <div style={styles.ownerMenu} role="menu">
            <button type="button" role="menuitem" style={styles.ownerMenuItem} onClick={() => { setShowOwnerMenu(false); setShowEditModal(true); }}>✏️ Edit</button>
            <button type="button" role="menuitem" style={{ ...styles.ownerMenuItem, color: '#FCA5A5' }} onClick={handleDeleteCurrent}>🗑️ Delete</button>
          </div>
        )}
      </div>
    );
  };

  const handleSwipe = (direction: 'up' | 'down') => {
    if (direction === 'up' && currentIndex < videos.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else if (direction === 'down' && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      handleSwipe('down');
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      handleSwipe('up');
    }
    if (e.key === ' ') {
      e.preventDefault();
      const video = videoRefs.current[currentIndex];
      if (video?.paused) video.play();
      else video?.pause();
    }
    if (e.key === 'f' || e.key === 'F') {
      e.preventDefault();
      const video = videoRefs.current[currentIndex];
      if (video && document.fullscreenElement !== video) {
        video.requestFullscreen?.().catch(err => console.warn('Fullscreen failed:', err));
      } else if (document.fullscreenElement) {
        document.exitFullscreen?.();
      }
    }
  };

  const currentVideo = videos[currentIndex];

  useEffect(() => {
    setShowPlaybackOptions(false);
    setShowOwnerMenu(false);
  }, [currentVideo?.id]);

  // If a deep-link videoId is provided in the URL, load that single Pulse
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const vid = sp.get('videoId');
    if (!vid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getPulseById(vid);
        const pv = res.pulse as any as PulseVideo;
        if (!cancelled) {
          setVideos([pv]);
          setCurrentIndex(0);
        }
      } catch (e) {
        console.warn('Failed to load pulse deep link', e);
      }
    })();
    return () => { cancelled = true; };
  }, [location.search]);

  useEffect(() => {
    // Prefill edit fields when current video changes
    if (currentVideo) {
      setEditCaption(currentVideo.caption || '');
      setEditAudience((((currentVideo as any).audience || 'public') as 'public' | 'friends'));
      setEditCategory((currentVideo.category as Category) || 'Messages');
    }
  }, [currentVideo]);

  const handleSubmitRepost = async () => {
    if (!auth.currentUser || !repostTarget || postingRepost) return;
    setPostingRepost(true);
    try {
      const original: any = {
        id: repostTarget.id,
        userId: repostTarget.userId,
        userName: repostTarget.userName,
        userPhoto: repostTarget.userPhoto || '',
        content: repostTarget.caption || '',
        videoURL: repostTarget.videoURL,
        createdAt: (repostTarget as any).createdAt || new Date(),
      };
      await createPost({
        content: repostQuote.trim(),
        type: 'repost',
        repostOf: repostTarget.id,
        original,
        moderation: { status: 'approved', reason: null, scores: {} },
        userName: auth.currentUser.displayName || 'Anonymous',
        userPhoto: (await getMyPhoto()),
      });
      setShowRepostModal(false);
      setRepostTarget(null);
      setRepostQuote('');
      alert('Reposted to your timeline');
    } catch (e) {
      console.error('Repost failed', e);
      alert('Failed to repost');
    } finally {
      setPostingRepost(false);
    }
  };

  return (
    <div style={styles.container} onKeyDown={handleKeyDown} tabIndex={0}>
      <TopNavigation />
      <header style={styles.pageHeader}>
        <h1 style={styles.logo}>Pulse</h1>
        <button
          onClick={() => setShowUploadModal(true)}
          style={styles.uploadButton}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 14px rgba(102,234,209,0.25)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
          onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
          onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
        >
          + Upload
        </button>
      </header>

      <div style={styles.subtitle}>
        <p style={styles.subtitleText}>Share the Vibe 🎬</p>
      </div>

      <div style={isMobile ? { ...styles.categoriesBar, flexWrap: 'nowrap', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' } : styles.categoriesBar}>
        {categories.map((cat) => {
          const active = selectedCategory === cat;
          return (
            <button
              key={cat}
              aria-pressed={active}
              onClick={() => setSelectedCategory(cat)}
              style={{
                ...styles.categoryChip,
                ...(isMobile ? styles.categoryChipMobile : {}),
                ...(active ? styles.categoryChipActive : {})
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.backgroundColor = '#EEF2F6';
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.backgroundColor = '#FFFFFF';
                }
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = 'scale(0.94)';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'none';
              }}
            >
              <span style={styles.categoryLabel}>{cat}</span>
            </button>
          );
        })}
      </div>

      <div style={{ ...styles.videoContainer, ...(isMobile ? styles.videoContainerMobile : {}) }}>
        {videos.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyText}>No videos in this category yet.</p>
            <p style={styles.emptySubtext}>Be the first to share!</p>
            <p style={{ fontSize: '12px', marginTop: '16px', opacity: 0.6 }}>
              💡 Tips: Use arrow keys (↑↓) to navigate • Space to play/pause • F for fullscreen
            </p>
          </div>
        ) : currentVideo ? (
          <div
            style={{ ...styles.videoWrapper, ...(isMobile ? styles.videoWrapperMobile : {}) }}
            onClick={() => {
              const video = videoRefs.current[currentIndex];
              if (!video) return;
              userInteractedRef.current = true;
              if (video.paused) {
                video.play().catch(() => {});
                try {
                  if (musicAudioRef.current) {
                    musicAudioRef.current.play?.();
                  } else {
                    // audio not ready yet; mark to start when loaded
                    pendingAudioStartRef.current = true;
                  }
                } catch {}
              } else {
                try { video.pause(); } catch {}
                try { musicAudioRef.current?.pause?.(); } catch {}
              }
            }}
          >
            <AmbientBackdrop />
            <WatchTracker key={currentVideo.id} contentType="pulse" contentId={currentVideo.id} />
            <SignedVideo
              ref={(el) => {
                videoRefs.current[currentIndex] = el;
              }}
              src={currentVideo.videoURL}
              poster={currentVideo.thumbnailURL || undefined}
              style={{ ...styles.video, objectFit: videoFit[currentVideo.id] || 'contain' }}
              onLoadedMetadata={(e) => {
                const v = e.currentTarget as HTMLVideoElement;
                const ratio = v.videoWidth && v.videoHeight ? v.videoWidth / v.videoHeight : 0;
                const id = currentVideo.id;
                setVideoFit((prev) => ({ ...prev, [id]: ratio > 0.5 && ratio < 0.62 ? 'cover' : 'contain' }));
              }}
              loop
              playsInline
              controls
              preload="auto"
              onClick={(e) => {
                const video = e.currentTarget as HTMLVideoElement;
                userInteractedRef.current = true;
                if (video.paused) {
                  video.play().catch(() => {});
                  try { musicAudioRef.current?.play?.(); } catch {}
                } else {
                  try { video.pause(); } catch {}
                  try { musicAudioRef.current?.pause?.(); } catch {}
                }
              }}
              onPlay={async () => {
                try {
                  const v = videos[currentIndex];
                  if (v && !viewedOnceRef.current.has(v.id)) {
                    viewedOnceRef.current.add(v.id);
                    await viewPulse(v.id);
                    setVideos((prev) => prev.map((p) => p.id === v.id ? { ...p, views: (p.views || 0) + 1 } : p));
                  }
                } catch {}
              }}
            />
            {/* Audience badge in corner */}
            <div style={styles.audienceCorner}>
              <span style={styles.audiencePill}>
                {(((currentVideo as any).audience || 'public') === 'public') ? '🌍 Public' : '👥 Friends'}
              </span>
            </div>
            {renderActionButtons()}
            {(showInfoOverlay || !isMobile) && (
            <div
              style={{
                ...styles.videoOverlay,
                ...(isMobile ? styles.videoOverlayMobile : { bottom: 56 })
              }}
              onClick={(e) => { if (isMobile) { e.stopPropagation(); } }}
            >
              <div style={styles.videoInfo}>
                <div style={styles.userInfo}>
                  <ImageWithFallback
                    src={currentVideo.userPhoto || 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><rect width="100%" height="100%" fill="%2366EAD1"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23072B2C" font-size="18">?</text></svg>'}
                    alt=""
                    style={styles.userAvatar}
                  />
                  <UserLink userId={currentVideo.userId} as="strong" style={styles.userName}>{currentVideo.userName}</UserLink>
                </div>
                <p style={styles.caption}>{renderTextWithLinks(currentVideo.caption)}</p>
                <p style={styles.category}>#{currentVideo.category}</p>
              </div>

              {showPlaybackOptions && (
                <div
                  role="dialog"
                  aria-label="Playback options"
                  style={{
                    ...styles.playbackPanel,
                    ...(isMobile ? styles.playbackPanelMobile : {})
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={styles.playbackPanelTitleRow}>
                    <span style={styles.playbackPanelTitle}>Playback</span>
                    <button
                      aria-label="Close playback options"
                      onClick={(e) => { e.stopPropagation(); setShowPlaybackOptions(false); }}
                      style={styles.playbackCloseButton}
                    >
                      ✕
                    </button>
                  </div>
                  <div style={styles.playbackPanelLabel}>Speed</div>
                  <div style={styles.playbackRateRow}>
                    {PLAYBACK_SPEEDS.map((speed) => (
                      <button
                        key={speed}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPlaybackRate(speed);
                        }}
                        style={{
                          ...styles.playbackRateButton,
                          ...(Math.abs(playbackRate - speed) < 0.001 ? styles.playbackRateButtonActive : {})
                        }}
                      >
                        {`${speed}x`}
                      </button>
                    ))}
                  </div>
                  <div style={styles.playbackPanelLabel}>Jump</div>
                  <div style={styles.skipRow}>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSkip(-10); }}
                      style={styles.skipButton}
                    >
                      ⏪ 10s
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSkip(10); }}
                      style={styles.skipButton}
                    >
                      10s ⏩
                    </button>
                  </div>
                  <p style={styles.playbackHint}>Speed syncs the reel and soundtrack.</p>
                </div>
              )}
            </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Floating create button to ensure persistent access on mobile */}
      {!showUploadModal && (
        <button
          aria-label="Create Pulse"
          onClick={() => setShowUploadModal(true)}
          style={{ ...styles.fab, ...(isMobile ? { bottom: 88 } : {}) }}
          className="pulse-fab"
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'none'; }}
          onMouseDown={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.98)'; }}
          onMouseUp={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)'; }}
        >
          ➕
        </button>
      )}

      {showUploadModal && (
        <UploadModal onClose={() => setShowUploadModal(false)} initialCategory={selectedCategory} />
      )}
      {showRepostModal && (
        <div style={styles.repostOverlay} onClick={() => setShowRepostModal(false)}>
          {/* Desktop: side panel; Mobile: centered modal */}
          <div
            style={isMobile ? styles.repostModal : styles.sidePanel}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: 0, fontSize: 18 }}>Repost Pulse</h3>
            <p style={{ margin: '8px 0 12px', fontSize: 12, color: '#555' }}>Optional: add a comment</p>
            <textarea
              value={repostQuote}
              onChange={(e) => setRepostQuote(e.target.value)}
              rows={3}
              placeholder="Say something about this... (optional)"
              style={{ width: '100%', border: '1px solid #ddd', borderRadius: 8, padding: 10, resize: 'vertical', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, gap: 8 }}>
              <button onClick={() => setShowRepostModal(false)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSubmitRepost} disabled={postingRepost} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#0B6B6F', color: '#fff', cursor: 'pointer' }}>{postingRepost ? 'Reposting…' : 'Repost'}</button>
            </div>
          </div>
        </div>
      )}
      {showEditModal && currentVideo && (
        <div style={styles.repostOverlay} onClick={() => setShowEditModal(false)}>
          <div style={styles.repostModal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: 0, fontSize: 18 }}>Edit Pulse</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0' }}>
              <label htmlFor="edit-audience" style={{ fontSize: 12, color: '#555' }}>Audience</label>
              <select id="edit-audience" value={editAudience} onChange={(e) => setEditAudience(e.target.value as 'public' | 'friends')} style={{ padding: '4px 6px', borderRadius: 6, border: '1px solid #ccc' }}>
                <option value="public">Public</option>
                <option value="friends">Friends</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <label htmlFor="edit-category" style={{ fontSize: 12, color: '#555' }}>Category</label>
              <select id="edit-category" value={editCategory} onChange={(e) => setEditCategory(e.target.value as Category)} style={{ padding: '4px 6px', borderRadius: 6, border: '1px solid #ccc' }}>
                {categories.filter(c => c !== 'For You' && c !== 'My Videos').map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <textarea
              value={editCaption}
              onChange={(e) => setEditCaption(e.target.value)}
              rows={3}
              placeholder="Edit caption"
              style={{ width: '100%', border: '1px solid #ddd', borderRadius: 8, padding: 10, resize: 'vertical', fontFamily: 'inherit' }}
            />
            <ThumbnailPicker file={editThumb} onChange={setEditThumb} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, gap: 8 }}>
              <button onClick={() => setShowEditModal(false)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button
                onClick={async () => {
                  try {
                    const thumb = editThumb ? await uploadThumbnail(editThumb) : null;
                    await updatePulse(currentVideo.id, {
                      caption: editCaption,
                      audience: editAudience,
                      category: editCategory,
                      ...(thumb ? { thumbnailURL: thumb.thumbnailURL, moderationTokens: { thumbnailURL: thumb.proof } } : {}),
                    });
                    setVideos((prev) => prev.map((v) => v.id === currentVideo.id ? { ...v, caption: editCaption, category: editCategory, ...(thumb ? { thumbnailURL: thumb.thumbnailURL } : {}) } as any : v));
                    setEditThumb(null);
                    setShowEditModal(false);
                  } catch (e) {
                    console.error('Edit pulse failed', e);
                    alert('Failed to save changes');
                  }
                }}
                style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#0B6B6F', color: '#fff', cursor: 'pointer' }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {showAdOverlay && (
        <AdSlot
          placement="midroll_pulse"
          variant="overlay"
          contentType="pulse"
          contentId={videos[currentIndex]?.id}
          autoCloseSeconds={4}
          onComplete={() => {
            setShowAdOverlay(false);
            if (currentIndex < videos.length - 1) {
              setCurrentIndex((i) => Math.min(i + 1, videos.length - 1));
            }
          }}
        />
      )}

      {showComments && currentVideo && (
        <div
          style={isMobile ? styles.commentsSheetMobile : styles.commentsSheetDesktop}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={styles.commentsHeader}>
            <div style={styles.commentsHandle} />
            <div style={{ flex: 1 }} />
            <button aria-label="Close comments" onClick={() => setShowComments(false)} style={styles.commentsCloseBtn}>✕</button>
          </div>
          <div style={styles.commentsBody}>
            <CommentsInline contentId={currentVideo.id} contentType="pulse" contentOwnerId={currentVideo.userId} />
          </div>
        </div>
      )}
    </div>
  );
};

const UploadModal: React.FC<{ onClose: () => void; initialCategory: Category }> = ({ onClose, initialCategory }) => {
  const [caption, setCaption] = useState('');
  const [audience, setAudience] = useState<'public' | 'friends'>('public');
  // Default to Messages if initialCategory is not uploadable
  const getDefaultCategory = (cat: Category): Category => {
    if (cat === 'For You' || cat === 'My Videos') return 'Messages';
    return cat;
  };
  const [category, setCategory] = useState<Category>(getDefaultCategory(initialCategory));
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbFile, setThumbFile] = useState<File | null>(null);
  // Single input ref (native chooser will present camera or gallery options on mobile)
  const mediaInputRef = React.useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [selectError, setSelectError] = useState<string | null>(null);
  const [showQuickTrim, setShowQuickTrim] = useState(false);
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const MAX_REEL_SECONDS = 120;
  const [bgMusic, setBgMusic] = useState<MusicTrack | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  // Mentions/hashtags suggestions
  const [following, setFollowing] = useState<string[]>([]);
  const [mentionCandidates, setMentionCandidates] = useState<Array<{ id: string; name: string; username?: string; photoURL?: string }>>([]);
  const [suggestions, setSuggestions] = useState<{ open: boolean; kind: 'mention' | 'hashtag' | null; query: string; start: number }>({ open: false, kind: null, query: '', start: -1 });
  const captionRef = React.useRef<HTMLTextAreaElement | null>(null);
  // rightsConfirmed states removed (terms handled elsewhere)
  
  // Initialize default audience from user settings
  useEffect(() => {
    const loadDefault = async () => {
      if (!auth.currentUser) return;
      try {
        const settings = await getSettings();
        if (settings.settings.defaultAudience === 'friends' || settings.settings.defaultAudience === 'public') {
          setAudience(settings.settings.defaultAudience);
        }
        const followRes = await listFollowing(auth.currentUser.uid, { limit: 500 });
        setFollowing(followRes.users.map((u) => u.id));
      } catch {}
    };
    loadDefault();
  }, []);

  // Build mention candidates from following ids
  useEffect(() => {
    const run = async () => {
      try {
        if (!following || following.length === 0) { setMentionCandidates([]); return; }
        const res = await listFollowing(auth.currentUser?.uid || '', { limit: 500 });
        setMentionCandidates(res.users.map((u) => ({ id: u.id, name: u.name || 'Anonymous', username: u.username || '', photoURL: u.photoURL || '' })));
      } catch {
        setMentionCandidates([]);
      }
    };
    run();
  }, [following]);

  const getVideoDuration = (file: File) => new Promise<number>((resolve, reject) => {
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.src = url;
      v.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        const dur = v.duration;
        // Some containers report NaN or Infinity before ready; guard and retry once
        if (!isFinite(dur) || dur <= 0) {
          setTimeout(() => {
            const d2 = v.duration;
            resolve(isFinite(d2) && d2 > 0 ? d2 : 0);
          }, 50);
        } else {
          resolve(dur);
        }
      };
      v.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Could not load video metadata'));
      };
    } catch (err) { reject(err as any); }
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectError(null);
      setVideoFile(file);
      // Allow user to choose Quick Share or open Editor (no auto open)
      // Compute duration in the background; do not block UI
      try {
        const d = await getVideoDuration(file);
        setVideoDuration(d);
      } catch (err: any) {
        setVideoDuration(null);
        setSelectError(err?.message || 'Failed to read video');
      }
    }
  };

  // Removed unused handleRemoveSelectedVideo (cleanup)

  const handleUpload = async () => {
    if (!videoFile || !auth.currentUser) return;

    setUploading(true);
    try {
      let d = videoDuration;
      if (d == null) {
        try { d = await getVideoDuration(videoFile); } catch {}
      }
      // If over max duration, route user to full editor to trim instead of quick trim
      if (typeof d === 'number' && isFinite(d) && d > MAX_REEL_SECONDS + 0.5) {
        setShowEditor(true);
        return;
      }

      const hashtags = extractHashtags(caption);
      const mentions = extractMentions(caption);
      const filteredMentions = mentions.filter((m) => m !== 'followers');

      setUploadProgress(0);
      const upload = await uploadModeratedMedia({
        folder: 'pulseVideos',
        field: 'videoURL',
        file: videoFile,
        contentType: videoFile.type,
        metadata: {
          caption,
          category,
          hashtags: hashtags.join(','),
          mentions: mentions.join(','),
          audience,
        },
        onProgress: (pct) => setUploadProgress(pct),
      });

      const thumb = thumbFile ? await uploadThumbnail(thumbFile) : null;
      const moderationTokens: Record<string, unknown> = { videoURL: upload.proof, ...(thumb ? { thumbnailURL: thumb.proof } : {}) };

      const created = await createPulse({
        videoURL: upload.objectKey,
        thumbnailURL: thumb?.thumbnailURL || null,
        mediaPath: upload.storagePath,
        caption,
        category,
        audience,
        hashtags,
        mentions,
        moderationTokens,
        bgMusic: bgMusic ? { id: bgMusic.id, title: bgMusic.title, artistId: bgMusic.artistId, artistName: bgMusic.artistName, audioURL: bgMusic.audioURL } : null,
        moderation: {
          status: 'approved',
          reason: null,
          scores: { videoURL: Number(upload.proof.nsfwScore.toFixed(4)) },
        },
        userName: auth.currentUser.displayName || 'Anonymous',
        userPhoto: (await getMyPhoto()),
      });
      notifyIfHeldForReview((created as any)?.pulse);

      // Notify mentioned users (best-effort)
      try {
        for (const uname of filteredMentions) {
          const res = await getUserByUsername(uname).catch(() => null);
          const mentionedUserId = res?.profile?.uid;
          if (mentionedUserId && mentionedUserId !== auth.currentUser!.uid) {
            await createNotification(
              mentionedUserId,
              'mention',
              auth.currentUser!.uid,
              auth.currentUser!.displayName || 'Someone',
              (await getMyPhoto()),
              `${auth.currentUser!.displayName || 'Someone'} mentioned you in a Pulse`,
              created.id,
              'pulse'
            );
          }
        }
      } catch (e) {
        console.warn('Failed sending mention notifications', e);
      }

      onClose();
      setUploadProgress(0);
    } catch (error) {
      console.error('Upload failed:', error);
      const friendly = describeModerationError(error);
      if (friendly) {
        alert(friendly);
      } else {
        alert('Failed to upload video');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Post to Pulse</h3>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.modalContent}>
          {selectError && (
            <div style={{ marginBottom: '10px', color: '#b71c1c' }}>{selectError}</div>
          )}
          
          {/* Single chooser input (images or videos) */}
          <input
            type="file"
            accept="video/*"
            onChange={handleFileSelect}
            style={styles.fileInput2}
            disabled={uploading}
            ref={mediaInputRef}
          />
          
          <select value={category} onChange={(e) => setCategory(e.target.value as Category)} style={styles.select}>
            {categories.filter(c => c !== 'For You' && c !== 'My Videos').map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <label htmlFor="pulse-audience" style={{ fontSize: 12, color: '#9aa0a6' }}>Audience</label>
            <select
              id="pulse-audience"
              value={audience}
              onChange={(e) => setAudience(e.target.value as 'public' | 'friends')}
              style={{
                padding: '4px 6px',
                borderRadius: 6,
                border: '1px solid #ccc',
                background: '#fff',
                color: '#072B2C',
                outline: 'none',
                fontSize: 12,
                lineHeight: '16px',
                height: 28
              }}
            >
              <option value="public">Public</option>
              <option value="friends">Friends</option>
            </select>
          </div>

          <textarea
            value={caption}
            onChange={(e) => {
              const val = e.target.value;
              setCaption(val);
              const el = captionRef.current;
              const caret = el ? el.selectionStart || val.length : val.length;
              const upto = val.slice(0, caret);
              const tokenMatch = upto.match(/(^|\s)([@#][a-zA-Z0-9_]*)$/);
              if (tokenMatch) {
                const token = tokenMatch[2] || '';
                const kind = token.startsWith('@') ? 'mention' : 'hashtag';
                const queryTxt = token.slice(1).toLowerCase();
                setSuggestions({ open: queryTxt.length >= 1, kind, query: queryTxt, start: caret - token.length });
              } else {
                setSuggestions({ open: false, kind: null, query: '', start: -1 });
              }
            }}
            placeholder="Add a caption..."
            style={styles.textarea}
            rows={3}
            ref={captionRef}
          />
          <ThumbnailPicker file={thumbFile} onChange={setThumbFile} disabled={uploading} videoFile={videoFile} />
          {suggestions.open && (
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', zIndex: 20, background: '#fff', border: '1px solid #E0E0E0', borderRadius: 8, marginTop: 6, padding: 6, maxHeight: 180, overflowY: 'auto', width: '100%' }}>
                {suggestions.kind === 'mention' ? (
                  <>
                    {'followers'.includes(suggestions.query) && (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          const before = caption.slice(0, suggestions.start);
                          const after = caption.slice(suggestions.start + suggestions.query.length + 1);
                          setCaption(`${before}@followers${after}`);
                          setSuggestions({ open: false, kind: null, query: '', start: -1 });
                        }}
                        style={{ padding: '6px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                      >
                        <span style={{ fontWeight: 700 }}>@followers</span>
                        <span style={{ fontSize: 12, color: '#9aa0a6' }}>Notify all your followers</span>
                      </div>
                    )}
                    {mentionCandidates
                      .filter((u) => (u.username || '').toLowerCase().includes(suggestions.query) || (u.name || '').toLowerCase().includes(suggestions.query))
                      .slice(0, 6)
                      .map((u) => (
                        <div
                          key={u.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            const before = caption.slice(0, suggestions.start);
                            const after = caption.slice(suggestions.start + suggestions.query.length + 1);
                            const insert = `@${(u.username || u.name || '').replace(/\s+/g, '')}`;
                            setCaption(`${before}${insert}${after}`);
                            setSuggestions({ open: false, kind: null, query: '', start: -1 });
                          }}
                          style={{ padding: '6px 8px', cursor: 'pointer' }}
                        >
                          @{u.username || u.name}
                        </div>
                      ))}
                    {mentionCandidates.filter((u) => (u.username || '').toLowerCase().includes(suggestions.query) || (u.name || '').toLowerCase().includes(suggestions.query)).length === 0 && (
                      <div style={{ padding: '6px 8px', color: '#9aa0a6' }}>No matches</div>
                    )}
                  </>
                ) : (
                  <>
                    {Array.from(new Set([]))
                      .filter((t) => String(t).toLowerCase().includes(suggestions.query))
                      .slice(0, 8)
                      .map((t) => (
                        <div
                          key={t as string}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            const before = caption.slice(0, suggestions.start);
                            const after = caption.slice(suggestions.start + suggestions.query.length + 1);
                            setCaption(`${before}#${t}${after}`);
                            setSuggestions({ open: false, kind: null, query: '', start: -1 });
                          }}
                          style={{ padding: '6px 8px', cursor: 'pointer' }}
                        >
                          #{t as string}
                        </div>
                      ))}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Terms notice and checkbox removed per request. Terms are available on the Terms page. */}

          {/* Action buttons removed per request; editor opens automatically after selection */}

          {videoFile && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button onClick={handleUpload} disabled={uploading} style={styles.submitButton}>
                {uploading ? `Sharing... ${Math.round(uploadProgress)}%` : 'Share'}
              </button>
              <button onClick={() => setShowEditor(true)} disabled={uploading} style={styles.editButton}>
                {showEditor ? 'Editing…' : 'Open Editor / Add Music'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    {showEditor && videoFile && (
      <ReelEditorV2
        open={showEditor}
        videoFile={videoFile as File}
        onClose={() => setShowEditor(false)}
        onSave={(blob) => {
          const edited = new File([blob], `${Date.now()}_pulse.webm`, { type: blob.type || 'video/webm' });
          setVideoFile(edited);
          setShowEditor(false);
          setVideoDuration(null); // recompute on upload
        }}
        maxDurationSeconds={MAX_REEL_SECONDS}
        onBgMusicChange={(track) => setBgMusic(track)}
        initialCaption={caption}
        onCaptionChange={(c) => setCaption(c)}
      />
    )}
    {showQuickTrim && videoFile && (
      <QuickTrimModal
        open={showQuickTrim}
        videoFile={videoFile as File}
        onClose={() => setShowQuickTrim(false)}
        onSave={(blob) => {
          const edited = new File([blob], `${Date.now()}_trim.webm`, { type: (blob as any).type || 'video/webm' });
          setVideoFile(edited);
          setShowQuickTrim(false);
          setVideoDuration(null); // duration will change; optional to recompute
          // Auto-open full editor after trimming
          setShowEditor(true);
        }}
        maxDurationSeconds={MAX_REEL_SECONDS}
      />
    )}
  </>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F7F7F7',
    outline: 'none',
    paddingTop: '16px',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px 30px',
    backgroundColor: '#0B6B6F',
    color: '#FFFFFF',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  logo: {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
  },
  uploadButton: {
    padding: '8px 16px',
    backgroundColor: '#66EAD1',
    color: '#072B2C',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  subtitle: {
    backgroundColor: '#17A2A9',
    padding: '10px',
    textAlign: 'center',
  },
  subtitleText: {
    margin: 0,
    fontSize: '16px',
    color: '#FFFFFF',
    fontWeight: '500',
  },
  categoriesBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    rowGap: '12px',
    padding: '12px 16px',
    backgroundColor: '#F3F4F6',
    borderTop: '1px solid #E5E7EB',
    borderBottom: '1px solid #E5E7EB',
  },
  categoryChip: {
    position: 'relative',
    backgroundColor: '#FFFFFF',
    color: '#072B2C',
    border: '1px solid #E5E7EB',
    borderRadius: 999,
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    lineHeight: '18px',
    letterSpacing: '0.3px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.18s ease, color 0.18s ease, transform 0.18s ease',
    userSelect: 'none' as const,
    outline: 'none',
  },
  categoryChipMobile: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
    padding: '5px 12px',
    fontSize: 12,
  },
  categoryChipActive: {
    backgroundColor: '#0B6B6F',
    color: '#FFFFFF',
    border: '1px solid #0B6B6F',
    boxShadow: '0 0 0 2px rgba(11,107,111,0.25)',
  },
  categoryLabel: {
    position: 'relative',
    top: 0,
  },
  videoContainer: {
    position: 'relative',
    width: '100%',
    height: 'calc(100vh - 180px)',
    backgroundColor: '#F7F7F7',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  videoContainerMobile: {
    height: 'calc(100dvh - 120px - var(--bottom-nav-space, 0px))',
    minHeight: 360,
    backgroundColor: '#000',
    alignItems: 'stretch',
    padding: 0,
  },
  emptyState: {
    textAlign: 'center',
    color: '#FFFFFF',
  },
  emptyText: {
    fontSize: '18px',
    marginBottom: '10px',
  },
  emptySubtext: {
    fontSize: '14px',
    color: '#AAA',
  },
  videoWrapper: {
    position: 'relative',
    height: '100%',
    maxHeight: '100%',
    aspectRatio: '9 / 16',
    maxWidth: '100%',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 12px 40px rgba(7,43,44,0.25)',
    background: '#072B2C',
  },
  videoWrapperMobile: {
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    aspectRatio: 'auto',
    borderRadius: 0,
    boxShadow: 'none',
  },
  actionRail: {
    position: 'absolute',
    right: 10,
    bottom: 72,
    zIndex: 6,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 14,
  },
  actionRailMobile: {
    right: 8,
    bottom: 84,
  },
  railButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: '#FFFFFF',
    minWidth: 48,
  },
  railIcon: {
    width: 46,
    height: 46,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 22,
    lineHeight: 1,
    background: 'rgba(0,0,0,0.38)',
    border: '1px solid rgba(255,255,255,0.18)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
  },
  railLabel: {
    fontSize: 12,
    fontWeight: 600,
    textShadow: '0 1px 3px rgba(0,0,0,0.7)',
  },
  ownerMenu: {
    position: 'absolute',
    right: 58,
    bottom: 0,
    minWidth: 140,
    background: 'rgba(9,16,18,0.94)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 12,
    padding: 6,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
  },
  ownerMenuItem: {
    background: 'none',
    border: 'none',
    color: '#E5F7F6',
    textAlign: 'left',
    padding: '10px 12px',
    borderRadius: 8,
    fontSize: 14,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    cursor: 'pointer',
    position: 'relative',
    display: 'block',
    backgroundColor: 'transparent',
  },
  videoMobile: {
    objectFit: 'cover',
    borderRadius: 0,
  },
  videoOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '20px 76px 20px 16px',
    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    pointerEvents: 'none',
  },
  videoInfo: {
    flex: 1,
    color: '#FFFFFF',
    pointerEvents: 'auto',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px',
  },
  userAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: '2px solid #66EAD1',
  },
  userName: {
    fontSize: '16px',
  },
  caption: {
    fontSize: '14px',
    marginBottom: '5px',
  },
  category: {
    fontSize: '12px',
    color: '#66EAD1',
    fontWeight: 'bold',
  },
  audienceCorner: {
    position: 'absolute',
    top: 10,
    left: 10,
    zIndex: 5,
  },
  audiencePill: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 999,
    background: 'rgba(0,0,0,0.6)',
    color: '#e8eaed',
    fontSize: 12,
    border: '1px solid rgba(255,255,255,0.2)'
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    pointerEvents: 'auto',
    position: 'relative',
  },
  actionButton: {
    background: 'none',
    border: 'none',
    color: '#FFFFFF',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transition: 'transform 0.2s',
    pointerEvents: 'auto',
  },
  actionIcon: {
    fontSize: '20px',
    marginBottom: '5px',
  },
  actionCount: {
    fontSize: '12px',
  },
  actionsMobile: {
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
    flexWrap: 'wrap',
    background: 'rgba(0,0,0,0.35)',
    padding: '6px 10px',
    borderRadius: 12,
    marginRight: 72,
  },
  playbackPanel: {
    position: 'absolute',
    right: 88,
    bottom: 12,
    width: 220,
    background: 'rgba(9,16,18,0.92)',
    borderRadius: 16,
    padding: '14px 16px',
    boxShadow: '0 10px 35px rgba(0,0,0,0.45)',
    color: '#E5F7F6',
    pointerEvents: 'auto',
    border: '1px solid rgba(255,255,255,0.08)',
    backdropFilter: 'blur(8px)'
  },
  playbackPanelMobile: {
    position: 'fixed',
    right: 16,
    left: 16,
    bottom: 90,
    width: 'auto',
    zIndex: 2100,
    maxWidth: 'calc(100% - 32px)'
  },
  playbackPanelTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12
  },
  playbackPanelTitle: {
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: '0.4px'
  },
  playbackCloseButton: {
    border: 'none',
    background: 'rgba(255,255,255,0.12)',
    color: '#E5F7F6',
    borderRadius: 999,
    width: 26,
    height: 26,
    cursor: 'pointer'
  },
  playbackPanelLabel: {
    fontSize: 11,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.8px',
    color: 'rgba(229,247,246,0.6)',
    marginBottom: 6
  },
  playbackRateRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 14
  },
  playbackRateButton: {
    flex: '0 0 calc(33% - 4px)',
    borderRadius: 10,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid transparent',
    color: '#F5FEFD',
    fontSize: 13,
    padding: '6px 0',
    cursor: 'pointer'
  },
  playbackRateButtonActive: {
    background: '#66EAD1',
    color: '#072B2C',
    borderColor: '#8AF4DE',
    fontWeight: 600
  },
  skipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 8
  },
  skipButton: {
    flex: 1,
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.15)',
    background: 'rgba(255,255,255,0.08)',
    color: '#F5FEFD',
    padding: '8px 0',
    fontSize: 13,
    cursor: 'pointer'
  },
  playbackHint: {
    margin: 0,
    marginTop: 6,
    fontSize: 11,
    color: 'rgba(229,247,246,0.65)'
  },
  repostModal: {
    width: '92%',
    maxWidth: 520,
    background: '#fff',
    borderRadius: 12,
    padding: 16,
    boxShadow: '0 12px 30px rgba(0,0,0,0.25)'
  },
  sidePanel: {
    width: 420,
    maxWidth: '90vw',
    height: 'calc(100vh - 120px)',
    marginTop: 80,
    marginRight: 12,
    background: '#fff',
    borderRadius: 12,
    padding: 16,
    boxShadow: '0 12px 30px rgba(0,0,0,0.25)'
  },
  swipeButton: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(255,255,255,0.3)',
    border: 'none',
    color: '#FFFFFF',
    fontSize: '24px',
    padding: '10px 20px',
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'background-color 0.2s, transform 0.2s',
  },
  swipeUp: {
    top: '20px',
  },
  swipeDown: {
    bottom: '100px',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000,
  },
  repostOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 2000,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'stretch',
  },
  videoOverlayMobile: {
    padding: '8px 72px 52px 12px',
    background: 'linear-gradient(transparent, rgba(0,0,0,0.55))',
  },
  
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #E0E0E0',
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#072B2C',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#5B6E6E',
  },
  modalContent: {
    padding: '20px',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    width: '92%',
    maxWidth: '600px',
    maxHeight: '85vh',
    overflow: 'auto',
    boxShadow: '0 12px 30px rgba(0,0,0,0.25)'
  },
  fileInput2: {
    width: '100%',
    padding: '10px',
    marginBottom: '15px',
    border: '2px solid #17A2A9',
    borderRadius: '8px',
    background: '#F9FFFF'
  },
  select: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '2px solid #17A2A9',
    marginBottom: '15px',
  },
  textarea: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '2px solid #17A2A9',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'vertical',
    marginBottom: '15px',
  },
  submitButton: {
    width: '100%',
    padding: '14px',
    backgroundColor: '#0B6B6F',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  editButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#072B2C',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    opacity: 0.92,
  },
  secondaryButton: {
    flex: 1,
    padding: '12px 20px',
    background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
    color: '#FFFFFF',
    border: '2px solid #FF6B35',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    textAlign: 'center',
    boxShadow: '0 4px 15px rgba(255, 107, 53, 0.3)',
  },
  fab: {
    position: 'fixed',
    right: 20,
    bottom: 20,
    width: 56,
    height: 56,
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#66EAD1',
    color: '#072B2C',
    fontSize: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 8px 18px rgba(102,234,209,0.25)',
    zIndex: 1500,
    transition: 'transform 0.2s',
  },
  commentsSheetMobile: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    height: '60vh',
    background: '#111',
    borderTopLeftRadius: 14,
    borderTopRightRadius: 14,
    boxShadow: '0 -10px 30px rgba(0,0,0,0.5)',
    zIndex: 2000,
    display: 'flex',
    flexDirection: 'column',
  },
  commentsSheetDesktop: {
    position: 'fixed',
    right: 12,
    top: 80,
    width: 420,
    maxWidth: '90vw',
    bottom: 16,
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 12px 30px rgba(0,0,0,0.25)',
    zIndex: 2000,
    display: 'flex',
    flexDirection: 'column',
  },
  commentsHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
  },
  commentsHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    background: '#ccc',
    margin: '8px auto',
  },
  commentsCloseBtn: {
    background: 'none',
    border: '1px solid #ccc',
    color: '#072B2C',
    borderRadius: 8,
    padding: '6px 10px',
    cursor: 'pointer',
  },
  commentsBody: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 12px 12px',
  },
};

export default Pulse;


